import { CalendarPlus, Loader2 } from "lucide-react";
import { Field, formatDateTime } from "./ui";
import ScheduleDemoForm from "./ScheduleDemoForm";
import { formatZonedDate, formatTimeRange, formatDisplayTimezoneLabel, BROWSER_TIMEZONE } from "@/lib/timezones";

export default function DemoRequestSchedulingSection({
  current,
  schedulingMode,
  setSchedulingMode,
  onScheduled,
  cancelConfirmOpen,
  setCancelConfirmOpen,
  cancelDemo,
  cancelSaving,
  error,
}) {
  return (
    <>
      <section data-testid="demo-scheduling-section">
        {schedulingMode ? (
          <ScheduleDemoForm
            demoRequestId={current.demo_request_id}
            existing={schedulingMode === "reschedule" ? current.scheduled_demo : null}
            mode={schedulingMode}
            onCancel={() => setSchedulingMode(null)}
            onSaved={onScheduled}
          />
        ) : current.scheduled_demo && current.scheduled_demo.status === "scheduled" ? (
          <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-5 dark:border-violet-500/20 dark:bg-violet-500/10" data-testid="scheduled-demo-section">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-950 dark:text-white">Scheduled Demo</h3>
              {current.status === "Scheduled" && (
                <button
                  type="button"
                  onClick={() => setSchedulingMode("reschedule")}
                  data-testid="schedule-edit-btn"
                  className="text-xs font-bold text-[#0052FF] hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Edit
                </button>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Date" value={formatZonedDate(current.scheduled_demo.start_at, BROWSER_TIMEZONE)} testId="scheduled-date" />
              <Field label="Time" value={formatTimeRange(current.scheduled_demo.start_at, current.scheduled_demo.end_at, BROWSER_TIMEZONE, current.scheduled_demo.duration_minutes)} testId="scheduled-time" />
              <Field label="Timezone" value={formatDisplayTimezoneLabel(BROWSER_TIMEZONE)} testId="scheduled-timezone" />
              <Field label="Meeting type" value={current.scheduled_demo.meeting_type} testId="scheduled-meeting-type" />
              <Field label="Meeting link" value={current.scheduled_demo.meeting_url || current.scheduled_demo.meeting_location || "—"} testId="scheduled-meeting-link" />
              <Field label="Assigned to" value={current.scheduled_demo.assigned_admin_name} testId="scheduled-assigned" />
            </div>
            {current.status === "Scheduled" ? (
              <div className="mt-5 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSchedulingMode("reschedule")}
                  data-testid="reschedule-demo-btn"
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-white dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                >
                  Reschedule
                </button>
                <button
                  type="button"
                  onClick={() => setCancelConfirmOpen(true)}
                  data-testid="cancel-demo-btn"
                  className="rounded-xl border border-red-200 px-4 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
                >
                  Cancel Demo
                </button>
              </div>
            ) : (
              <p className="mt-4 text-xs font-semibold text-slate-500 dark:text-slate-400" data-testid="scheduled-demo-completed-note">This demo has been completed. Scheduling details are retained for your records.</p>
            )}
          </div>
        ) : current.status !== "Completed" && current.status !== "Declined" ? (
          <div>
            {current.scheduled_demo?.status === "cancelled" && (
              <p className="mb-3 text-xs font-semibold text-slate-500 dark:text-slate-400" data-testid="previous-demo-cancelled-note">
                A previous scheduled demo was cancelled on {formatDateTime(current.scheduled_demo.cancelled_at)}.
              </p>
            )}
            <button
              type="button"
              onClick={() => setSchedulingMode("schedule")}
              data-testid="schedule-demo-btn"
              className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700"
            >
              <CalendarPlus size={16} />
              Schedule Demo
            </button>
          </div>
        ) : null}
      </section>

      {cancelConfirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 p-4" data-testid="cancel-demo-confirm-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#111C33]">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">Cancel this demo?</h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              The scheduled meeting will be cancelled and this request will return to Contacted. This cannot be undone.
            </p>
            {error && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="cancel-demo-error">{error}</p>}
            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={cancelDemo}
                disabled={cancelSaving}
                data-testid="cancel-demo-confirm-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {cancelSaving && <Loader2 size={16} className="animate-spin" />}
                Yes, cancel demo
              </button>
              <button
                type="button"
                onClick={() => setCancelConfirmOpen(false)}
                disabled={cancelSaving}
                data-testid="cancel-demo-dismiss-btn"
                className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
              >
                Keep demo
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
